// 중앙 집중식 에러 처리 시스템

export interface ApiErrorState {
    isError: boolean;
    message: string;
    result_code: string;
    shouldRetry?: boolean;
    shouldLogout?: boolean;
    shouldRedirect?: boolean;
    redirectUrl?: string;
}



export interface ApiResponse {
    result_code: string;
    msg?: string;
    message?: string;
    [key: string]: any;
}

// 환경 감지 유틸리티
export const isClient = typeof window !== 'undefined' && typeof document !== 'undefined';
export const isServer = !isClient;

// 기본 성공 코드들
const SUCCESS_CODES = ['100', '509'];

// 특별히 처리해야 할 에러 코드들만 정의 (나머지는 기본 처리)
const SPECIAL_ERROR_HANDLERS: Record<string, (response: ApiResponse) => Partial<ApiErrorState>> = {
    // 로그아웃이 필요한 에러들
    "80015": () => ({ shouldRedirect: true, redirectUrl: "/" }), // v2 대상 유저가 아닙니다
    "80001": () => ({ shouldLogout: true }), // ACCESS TOKEN 만료

    // 재시도가 필요한 에러들
    // "80001": () => ({ shouldRetry: true }), // ACCESS TOKEN 만료
};

/**
 * API 응답의 result_code를 기반으로 에러 상태를 처리합니다.
 * 특별한 처리가 필요한 에러만 정의하고, 나머지는 기본 처리합니다.
 * @param result_code - API 응답의 result_code
 * @param response - 전체 API 응답 객체
 * @returns ApiErrorState - 에러 처리 상태
 */
export const handleApiError = (
    result_code: string,
    response?: ApiResponse
): ApiErrorState => {
    // 성공 코드인 경우
    if (SUCCESS_CODES.includes(result_code)) {
        return {
            isError: false,
            message: "성공",
            result_code,
        };
    }

    // 특별한 처리가 필요한 에러인 경우
    if (SPECIAL_ERROR_HANDLERS[result_code]) {
        const specialHandling = SPECIAL_ERROR_HANDLERS[result_code](response || { result_code });
        return {
            isError: true,
            message: response?.msg || response?.message || `에러가 발생했습니다. (${result_code})`,
            result_code,
            ...specialHandling,
        };
    }

    // 기본 에러 처리 (대부분의 에러는 여기서 처리됨)
    return {
        isError: false,
        message: response?.msg || response?.message || `알 수 없는 오류가 발생했습니다. (${result_code})`,
        result_code,
    };
};

/**
 * API 응답이 성공인지 확인합니다.
 * @param response - API 응답 객체
 * @returns boolean - 성공 여부
 */
export const isApiSuccess = (response: ApiResponse): boolean => {
    return SUCCESS_CODES.includes(response.result_code);
};

/**
 * API 응답이 에러인지 확인합니다.
 * @param response - API 응답 객체
 * @returns boolean - 에러 여부
 */
export const isApiError = (response: ApiResponse): boolean => {
    return !isApiSuccess(response);
};

/**
 * 클라이언트 사이드에서만 에러 이벤트를 발생시킵니다.
 * @param errorState - 에러 상태 객체
 */
export const dispatchApiErrorEvent = (errorState: ApiErrorState): void => {
    if (isClient && typeof CustomEvent !== 'undefined') {
        const errorEvent = new CustomEvent('apiError', {
            detail: {
                result_code: errorState.result_code,
                message: errorState.message,
                shouldRetry: errorState.shouldRetry,
                shouldLogout: errorState.shouldLogout,
                shouldRedirect: errorState.shouldRedirect,
                redirectUrl: errorState.redirectUrl,
            }
        });
        window.dispatchEvent(errorEvent);
    }
};

/**
 * 클라이언트 사이드에서만 사용하는 API 응답 처리 함수
 * 에러가 있는 경우 이벤트를 발생시킵니다.
 * @param response - API 응답 객체
 * @returns ApiErrorState - 에러 처리 상태
 */
export const processApiResponse = (response: ApiResponse): ApiErrorState => {
    const errorState = handleApiError(response.result_code, response);

    if (errorState.isError) {
        dispatchApiErrorEvent(errorState);
    }

    return errorState;
};

/**
 * 서버사이드에서 사용할 수 있는 에러 처리 함수입니다.
 * 에러 상태만 반환하고 이벤트는 발생시키지 않습니다.
 * @param response - API 응답 객체
 * @returns ApiErrorState - 에러 처리 상태
 */
export const processServerApiResponse = (response: ApiResponse): ApiErrorState => {
    return handleApiError(response.result_code, response);
};

/**
 * 서버사이드 에러를 클라이언트로 전달하기 위한 에러 객체를 생성합니다.
 * @param errorState - 에러 상태 객체
 * @returns any - 클라이언트에서 처리할 수 있는 에러 객체
 */
export const createServerError = (errorState: ApiErrorState): any => {
    return {
        message: errorState.message,
        result_code: errorState.result_code,
        shouldRetry: errorState.shouldRetry,
        shouldLogout: errorState.shouldLogout,
        shouldRedirect: errorState.shouldRedirect,
        redirectUrl: errorState.redirectUrl,
        isServerError: true
    };
};

/**
 * 서버사이드에서 발생한 에러를 클라이언트에서 처리할 수 있도록 변환합니다.
 * @param response - API 응답 객체
 * @returns any | null - 에러가 있으면 에러 객체, 없으면 null
 */
export const handleServerApiError = (response: ApiResponse): any | null => {
    const errorState = processServerApiResponse(response);

    if (errorState.isError) {
        return createServerError(errorState);
    }

    return null;
};

/**
 * 환경에 관계없이 안전하게 사용할 수 있는 에러 처리 함수
 * 클라이언트에서는 이벤트를 발생시키고, 서버에서는 에러 객체를 반환합니다.
 * @param response - API 응답 객체
 * @returns ApiErrorState | any | null - 환경에 따라 다른 반환값
 */
export const processApiResponseSafe = (response: ApiResponse): ApiErrorState | any | null => {
    if (isClient) {
        return processApiResponse(response);
    } else {
        return handleServerApiError(response);
    }
};

/**
 * 바이낸스 API 에러를 처리하는 전용 함수
 * 바이낸스의 에러 코드를 result_code로 변환하여 처리합니다.
 * @param errorData - 바이낸스 에러 데이터
 * @returns void
 */
export const handleBinanceError = (errorData: { code: number; msg?: string }): void => {
    if (isClient && typeof CustomEvent !== 'undefined') {
        const errorEvent = new CustomEvent('apiError', {
            detail: {
                result_code: String(errorData.code), // 바이낸스 코드를 result_code로 변환
                message: errorData.msg || 'API 요청이 실패했습니다.',
                msg: errorData.msg || 'API 요청이 실패했습니다.'
            }
        });
        window.dispatchEvent(errorEvent);
    }
};